import { Injectable, LOCALE_ID, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpClient,
} from '@angular/common/http';
import { catchError, Observable, of } from 'rxjs';
import { mergeMap, map } from 'rxjs/operators';
import * as uuid from 'uuid';

import { AppStorage } from '../enums/app-storage.enum';
import { Env } from '../interfaces/env.interface';
import { ENV_TOKEN } from '@app/app.tokens';

@Injectable()
export class ApiRequestInterceptor implements HttpInterceptor {
  private localeId = inject(LOCALE_ID);
  private env = inject<Env>(ENV_TOKEN);
  private http = inject(HttpClient);

  private apiUrl?: string;

  static getUdid(): string {
    try {
      let udid = localStorage?.getItem(AppStorage.Udid);
      if (!udid) {
        udid = uuid.v4();
        localStorage?.setItem(AppStorage.Udid, udid);
      }

      return udid;
    } catch (e) {
      console.error(e);

      return uuid.v4();
    }
  }

  private getApiUrl(): Observable<string> {
    if (this.apiUrl) {
      return of(this.apiUrl);
    }

    const handler$ = this.http.get<{ apiUrl: string }>('config.json');
    return handler$.pipe(
      map(({ apiUrl }) => {
        this.apiUrl = apiUrl ? apiUrl.trim() : this.env.apiUrl;
        return this.apiUrl;
      }),
      catchError(() => {
        this.apiUrl = this.env.apiUrl;
        return of(this.apiUrl);
      })
    );
  }

  private getIntl(): string {
    if (this.localeId === 'ru') {
      return 'ru-RU';
    }
    return this.localeId;
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    let url = request.url;
    let params = request.params;

    if (url.includes('config.json')) {
      return next.handle(request);
    }

    const udid = ApiRequestInterceptor.getUdid();
    if (udid) {
      params = params.set('udid', udid);
    }

    const intl = this.getIntl();
    if (intl) {
      params = params.set('intl', intl);
    }

    return this.getApiUrl().pipe(
      mergeMap((apiUrl) => {
        if (!url.startsWith('http') && !url.startsWith('/assets')) {
          url = apiUrl + url;
        }

        return next.handle(
          request.clone({
            url,
            params: params,
          })
        );
      })
    );
  }
}
